/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useMemo, useState} from 'react';
import {Typography} from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import {translate} from '@ion/core/src/locale/locale';
import {Devices, GenericObject} from '@ion/core/src/types/entities';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import TertiaryButton from '@ion/dls/src/components/buttons/TertiaryButton';
import styles from '@ion/dls/src/editors/editorStyles';
import useExtendStyles from '@ion/dls/src/hooks/useExtendStyles';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import JobConflictResolutionChooser from './JobConflictResolutionChooser';

type JobsConflictsTableProps = {
  selectedTasks: {
    tasks?: GenericObject[];
  };
  jobSearchEngineEnabled?: boolean;
  selectedDevices?: Devices;
  jobName?: string;
  isConflictsLoading?: boolean;
  conflicts?: {
    id: number;
    devices: {
      creator: string;
      age: string;
      jobName: string;
      jobId: number;
      task: string;
    }[];
  }[];
  setShowConflicts?: Function;
  selectedOptions?: GenericObject;
  setSelectedOptions?: Function;
  onCreateJobBackClick?: Function;
  setIsProcessButtonDisabled?: Function;
}

const styleOverrides = (theme: DLSTheme) => ({
  back : {
    display    : 'flex',
    alignItems : 'center',
    gap        : 4,
    ...dlsAlt('typography', 'label1'),
    color      : dlsColor('grey2'),
    cursor     : 'pointer'
  },
  title : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    padding        : '16px 24px',
    marginBottom   : '16px',
    position       : 'relative',
    borderBottom   : `solid 1px ${dlsColor('grey4')}`
  },
  close : {
    left     : 0,
    position : 'absolute'
  },
  actions : {
    width           : '100%',
    boxSizing       : 'border-box',
    zIndex          : 100,
    padding         : '4px 12px',
    borderTop       : `solid 1px ${dlsColor('grey4')}`,
    backgroundColor : dlsColor('grey5'),
    position        : 'sticky',
    bottom          : -1,
    left            : 0,
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'flex-end'
  }
});

export const JobsConflictsTable = ({
  selectedTasks,
  jobName = '',
  conflicts,
  setShowConflicts,
  selectedDevices,
  selectedOptions,
  setSelectedOptions,
  onCreateJobBackClick,
  setIsProcessButtonDisabled,
  isConflictsLoading = false,
  jobSearchEngineEnabled = false
}: JobsConflictsTableProps) => {
  const cautionTitle                = translate('jobcreate:caution');
  const createJob                   = translate('jobcreate:title');
  const dialogStyles                = useExtendStyles(styles, styleOverrides);
  const classes                     = dialogStyles();
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = function() {
    if (activeStep < conflicts.length - 1) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = function() {
    if (activeStep > 0) {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleClose = function() {
    onCreateJobBackClick?.();
    setSelectedOptions({});
    setShowConflicts(false);
    setIsProcessButtonDisabled(false);
  };

  const areAllConflictsSelected = useMemo(() => {
    return conflicts?.every((conflictGroup) =>
      conflictGroup?.every((conflict) => {
        const conflictKey = `${conflict?.id}-${conflict?.task}`;
        return selectedOptions[conflictKey]?.value !== undefined;
      })
    );
  }, [conflicts, selectedOptions]);

  const areAllConflictsSelectedForStep = useMemo(() => {
    const currentConflicts = conflicts?.[activeStep];
    if (!currentConflicts) return false;
    return currentConflicts?.every((conflict) => {
      const conflictKey = `${conflict?.id}-${conflict?.task}`;
      return selectedOptions[conflictKey]?.value !== undefined;
    });
  }, [conflicts, selectedOptions, activeStep]);

  useEffect(() => {
    setIsProcessButtonDisabled(!areAllConflictsSelected);
  }, [areAllConflictsSelected, setIsProcessButtonDisabled]);

  return (
    <>
      <Render.If condition={!isConflictsLoading}>
        <div className={classes.title}>
          <div className={classes.close}>
            <div onClick={handleClose} className={classes.back}>
              <ChevronLeft />
              <span>{createJob}</span>
            </div>
          </div>
          <Typography component='h1' variant='h2'>
            {cautionTitle}
          </Typography>
        </div>
      </Render.If>
      <div className={jobSearchEngineEnabled ? classes.formWrapperCtcAlt : classes.formWrapperCtc}>
        <Render.If condition={!isConflictsLoading}>
          <JobConflictResolutionChooser conflicts={conflicts}
            setSelectedOptions={setSelectedOptions}
            selectedDevices={selectedDevices}
            selectedOptions={selectedOptions}
            currentJobName={jobName}
            activeStep={activeStep}
            selectedTasks={selectedTasks} />
        </Render.If>
      </div>
      <Render.If condition={!isConflictsLoading && conflicts?.length > 1}>
        <div className={classes.actions}>
          <TertiaryButton onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<ChevronLeft />}>
            {translate('jobcreate:back')}
          </TertiaryButton>
          <Render.If condition={activeStep < (conflicts?.length ?? 0) - 1}>
            <PrimaryButton onClick={handleNext}
              disabled={!areAllConflictsSelectedForStep}
              endIcon={<ChevronRight />}>
              {translate('jobcreate:next')}
            </PrimaryButton>
          </Render.If>
        </div>
      </Render.If>
    </>
  );
};
export default JobsConflictsTable;
